import { FormattedMessage, useIntl } from 'react-intl';
import { useFormMethods } from '@hqo/multi-step-form';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useCallback } from 'react';
import { Button } from '@hqo/ui-atoms';
import { Plus } from 'lucide-react';
import { useCreateVisitFormV2Context } from '../../context/create-visit-form-v2.context';
import { CreateVisitFormV2Values, VisitorMode, Visitor } from '../../types/form-data.types';
import { VisitorModeToggle, OneVisitorForm, MultipleVisitorsForm, VisitLevelFields } from './components';
import { CheckInType } from './components/check-in-type/check-in-type';
import { AcknowledgmentText } from './components/acknowledgment-text/acknowledgment-text';
import { CurrentVisitors } from './current-visitors';
import { useVisitStepFieldsConfig } from '../../hooks/use-ui-metadata-field-config.hook';
import { useFlag } from 'hooks/use-flag.hook';
import { LDFlags } from 'shared/consts';
import { EntryOption } from './components/entry-option/entry-option';
import { useFieldMetadata } from 'store/ui-metadata/hooks';

const EMPTY_VISITOR: Visitor = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  photoUuid: '',
};

export const VisitStep = (): JSX.Element => {
  const { formatMessage } = useIntl();
  // eslint-disable-next-line operator-linebreak
  const { getVisitStepData, updateVisitStepData, editDataVersion, isEditMode } = useCreateVisitFormV2Context();
  const savedData = getVisitStepData();
  const fieldsConfig = useVisitStepFieldsConfig();
  const showUiMetadata = useFlag(LDFlags.SHOW_VISIT_FORM_UI_METADATA, false);
  const form = useFormMethods() as unknown as UseFormReturn<CreateVisitFormV2Values>;
  const visitorsEditingEnabled = useFieldMetadata('visitors_editing_enabled');

  const handleRemoveVisitor = useCallback(
    (index: number): void => {
      if (!savedData?.visitors) {
        return;
      }
      const updatedVisitors = savedData.visitors.filter((_, i) => i !== index);
      updateVisitStepData({
        ...savedData,
        visitors: updatedVisitors,
      });
    },
    [savedData, updateVisitStepData],
  );

  const handleModeChange = useCallback(
    (newMode: VisitorMode): void => {
      form.setValue('visitorMode', newMode, { shouldValidate: false, shouldDirty: true });

      // Reset visitors for the selected mode
      const initialVisitors = newMode === VisitorMode.SINGLE ? [EMPTY_VISITOR] : [];
      form.setValue('visitors', initialVisitors, { shouldValidate: false, shouldDirty: true });
      form.setValue('email_opt_in', false, { shouldValidate: false, shouldDirty: true });
      form.setValue('sms_opt_in', false, { shouldValidate: false, shouldDirty: true });

      // Clear any validation errors for visitors and nested fields when switching modes
      form.clearErrors('visitors');
      form.clearErrors('visitors.0');
    },
    [form],
  );

  // Add the current form visitor to the existing visitors list (edit mode only)
  const handleAddVisitor = useCallback(async (): Promise<void> => {
    if (!form) {
      return;
    }
    // Validate the current visitor fields
    const isValid = await form.trigger(['visitors.0.firstName', 'visitors.0.lastName', 'visitors.0.companyName']);
    if (!isValid) {
      return;
    }

    const formValues = form.getValues();
    const newVisitor = formValues.visitors[0];

    // Check if visitor has required data
    if (!newVisitor.firstName || !newVisitor.lastName) {
      return;
    }

    // Add to existing visitors
    const existingVisitors = savedData?.visitors || [];
    updateVisitStepData({
      ...savedData,
      visitorMode: VisitorMode.SINGLE,
      visitSummary: savedData?.visitSummary || '',
      visitors: [...existingVisitors, newVisitor],
    });

    // Reset the form for adding another visitor
    const { email_opt_in: emailOptIn, sms_opt_in: smsOptIn } = form.getValues();
    form.reset({
      visitorMode: VisitorMode.SINGLE,
      visitSummary: '',
      visitors: [EMPTY_VISITOR],
      email_opt_in: emailOptIn,
      sms_opt_in: smsOptIn,
    });
  }, [form, savedData, updateVisitStepData]);

  const {
    control,
    setValue,
    register,
    formState: { errors },
  } = form;

  const visitorMode = useWatch({ control, name: 'visitorMode' });
  const visitors = useWatch({ control, name: 'visitors' }) ?? [];

  return (
    <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6" data-cy="visit-step">
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h3 className="text-xl font-semibold">
            <FormattedMessage id="createVisitFormV2.visitStep.title" defaultMessage="Visitor information" />
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {formatMessage({
              id: 'createVisitFormV2.visitStep.description',
              defaultMessage: 'Provide details about who is visiting',
            })}
          </p>
        </div>

        <div className="space-y-4 sm:space-y-5">
          {/* Visitor mode toggle hidden on mobile — CSV upload (the main "Multiple" flow) is not practical on small devices */}
          {!isEditMode && (
            <div className="hidden sm:block">
              <VisitorModeToggle value={visitorMode} onChange={handleModeChange} />
            </div>
          )}

          {isEditMode && visitorsEditingEnabled && visitors.length > 0 && (
            <div className="space-y-4 sm:space-y-5">
              {visitors.length > 1 && (
                <h4 className="text-sm font-medium text-gray-700">
                  {formatMessage({
                    id: 'createVisitFormV2.visitStep.visitorsHeading',
                    defaultMessage: 'Visitors',
                  })}
                </h4>
              )}
              {visitors.map((_, index) => (
                <div key={`visitor-${String(index)}`} className="space-y-2">
                  {visitors.length > 1 && (
                    <p className="text-sm font-medium text-gray-800">
                      {formatMessage(
                        {
                          id: 'createVisitFormV2.visitStep.visitorNumberLabel',
                          defaultMessage: 'Visitor {n}',
                        },
                        { n: index + 1 },
                      )}
                    </p>
                  )}
                  <OneVisitorForm
                    control={control}
                    register={register}
                    errors={errors}
                    fieldsConfig={fieldsConfig}
                    setValue={setValue}
                    visitorIndex={index}
                    visitorOnly={visitors.length > 1}
                  />
                </div>
              ))}
              {visitors.length > 1 && (
                <VisitLevelFields
                  control={control}
                  register={register}
                  errors={errors}
                  fieldsConfig={fieldsConfig}
                  setValue={setValue}
                />
              )}
            </div>
          )}

          {isEditMode && !visitorsEditingEnabled && (
            <>
              {editDataVersion < 1 && <VisitorModeToggle value={visitorMode} onChange={handleModeChange} />}
              {editDataVersion > 0 && savedData?.visitors && savedData.visitors.length > 0 && (
                <CurrentVisitors savedData={savedData} onRemoveVisitor={handleRemoveVisitor} isEditMode={isEditMode} />
              )}
            </>
          )}

          {/* Hide add visitor form in edit mode */}
          {!isEditMode && (
            <>
              {editDataVersion > 0 && (
                <h4 className="text-sm font-medium text-gray-700">
                  {formatMessage({
                    id: 'createVisitFormV2.visitStep.addNewVisitor',
                    defaultMessage: 'Add a new visitor',
                  })}
                </h4>
              )}
              {visitorMode === VisitorMode.SINGLE ? (
                <OneVisitorForm
                  control={control}
                  register={register}
                  errors={errors}
                  fieldsConfig={fieldsConfig}
                  setValue={setValue}
                />
              ) : (
                <MultipleVisitorsForm control={control} setValue={setValue} fieldsConfig={fieldsConfig} />
              )}

              {/* Add visitor button */}
              {editDataVersion > 0 && visitorMode === VisitorMode.SINGLE && (
                <Button
                  type="button"
                  variant="outline"
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={handleAddVisitor}
                  className="w-full sm:w-auto"
                  data-cy="add-visitor-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {formatMessage({
                    id: 'createVisitFormV2.visitStep.addVisitorButton',
                    defaultMessage: 'Add visitor',
                  })}
                </Button>
              )}
            </>
          )}

          {showUiMetadata && <CheckInType config={fieldsConfig.checkInType} />}
          {showUiMetadata && (!isEditMode || visitorsEditingEnabled) && (
            <EntryOption hidden={!fieldsConfig.passEntryOptionEnabled} />
          )}
          <AcknowledgmentText acknowledgmentText={fieldsConfig.acknowledgmentText} />
        </div>
      </div>
    </div>
  );
};
